<template>
  <div ref="sectionRef" :class="$style.neighbourhoodSection">
    <NeighbourhoodHeader />
    <div :class="$style.personaToggle" role="group" aria-label="Select persona type">
      <button
        v-for="config in personaConfigs"
        :key="config.type"
        :class="[
          $style.personaButton,
          { [$style.active]: selectedPersona === config.type },
          { [$style.personaButtonSkeleton]: isLoading },
        ]"
        :disabled="isLoading"
        :aria-pressed="selectedPersona === config.type"
        :aria-label="
          t('neighbourhood.persona.ariaLabel', {
            persona: t(`neighbourhood.persona.${config.type}`).toLowerCase(),
          })
        "
        @click="handlePersonaClick(config.type)"
      >
        <NeighbourhoodIcon
          :name="getPersonaIcon(config.type)"
          size="large"
          :class="$style.personaIcon"
          aria-hidden="true"
        />
        <span>{{ t(`neighbourhood.persona.${config.type}`) }}</span>
      </button>
    </div>
    <div :class="$style.grayCard">
      <div v-if="isLoading" :class="$style.loadingSkeleton">
        <div :class="$style.skeletonHighlights">
          <div :class="$style.skeletonHighlightsHeader">
            <div :class="$style.skeletonCircle"></div>
            <div :class="$style.skeletonLineMedium"></div>
          </div>
          <div :class="$style.skeletonLine"></div>
          <div :class="$style.skeletonLineShort"></div>
        </div>
        <div :class="$style.skeletonTwoColumn">
          <div :class="$style.skeletonColumnCard">
            <div :class="$style.skeletonColumnTitle"></div>
            <div :class="$style.skeletonList">
              <div v-for="index in 3" :key="`dna-${index}`" :class="$style.skeletonListItem">
                <div :class="$style.skeletonIcon"></div>
                <div :class="$style.skeletonLine"></div>
              </div>
            </div>
          </div>
          <div :class="$style.skeletonColumnCard">
            <div :class="$style.skeletonColumnTitleWide"></div>
            <div :class="$style.skeletonPlacesList">
              <div v-for="index in 4" :key="`place-${index}`" :class="$style.skeletonPlaceItem">
                <div :class="$style.skeletonIcon"></div>
                <div :class="$style.skeletonPlaceLines">
                  <div :class="$style.skeletonLine"></div>
                  <div :class="$style.skeletonLineShort"></div>
                </div>
              </div>
            </div>
            <div :class="$style.skeletonBadges">
              <div :class="$style.skeletonBadge"></div>
              <div :class="$style.skeletonBadge"></div>
              <div :class="$style.skeletonBadge"></div>
            </div>
          </div>
        </div>
        <div :class="$style.skeletonStatsRow">
          <div :class="$style.skeletonStatCard"></div>
          <div :class="$style.skeletonStatCard"></div>
          <div :class="$style.skeletonStatCard"></div>
        </div>
        <div :class="$style.skeletonFeedback">
          <div :class="$style.skeletonLineMedium"></div>
          <div :class="$style.skeletonCircle"></div>
          <div :class="$style.skeletonCircle"></div>
        </div>
      </div>
      <div
        v-else-if="currentInsightsData"
        :key="`content-${contentKey}`"
        :class="{ [$style.fadeIn]: isMounted && !isLoading }"
      >
        <HighlightsSection :text="currentInsightsData.highlights" />
        <LifestyleDNAAndKeyPlaces
          :lifestyleDNA="currentInsightsData.lifestyleDNA"
          :keyPlaces="currentInsightsData.keyPlaces"
          :personaLabel="t(`neighbourhood.persona.${selectedPersona}`)"
          @explore="handleExploreNearby"
        />
        <StatsCardRow :stats="statsWithPlaceholder" />
        <FeedbackSection />
      </div>
      <div v-else :class="$style.noData">
        <p>{{ t('neighbourhood.insights.noData') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable vue/no-unused-properties */
import { computed, onBeforeUnmount, onMounted, PropType, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  PERSONA_STATS_TRANSLATION_KEYS,
  PLACEHOLDER_VALUES,
} from '@/helpers/Neighbourhood/neighbourhoodConstants';
import type { NeighbourhoodPoiDescriptionResponse } from '@/helpers/Neighbourhood/neighbourhoodPOITypes';
import {
  getPersonaConfig,
  PERSONA_CONFIGS,
  PersonaType,
} from '@/helpers/Neighbourhood/personaTypes';
import { transformPOIResponseToComponentFormat } from '@/helpers/Neighbourhood/poiResponseTransformer';
import { useNeighbourhoodStore } from '@/store/modules/neighbourhood';
import FeedbackSection from './FeedbackSection.vue';
import HighlightsSection from './HighlightsSection.vue';
import LifestyleDNAAndKeyPlaces from './LifestyleDNAAndKeyPlaces.vue';
import type { StatCard } from './neighbourhood-insights-types';
import {
  trackNeighbourhoodExploreNearby,
  trackNeighbourhoodFeatureDisplayed,
  trackNeighbourhoodPersonaChange,
} from './neighbourhood.tracking';
import NeighbourhoodHeader from './NeighbourhoodHeader.vue';
import NeighbourhoodIcon from './NeighbourhoodIcon.vue';
import StatsCardRow from './StatsCardRow.vue';

const { t } = useI18n();
const props = defineProps({
  neighbourhoodData: {
    type: Object as PropType<NeighbourhoodPoiDescriptionResponse>,
    required: false,
    default: null,
  },
  coordinates: {
    type: Object as PropType<{ lat: number; lng: number }>,
    required: false,
    default: null,
  },
  isLoading: {
    type: Boolean,
    required: false,
    default: false,
  },
  listingData: {
    type: Object as PropType<{
      municipalityId?: string;
      municipalityName?: string;
      propertyType?: string;
      zipCode?: string;
    }>,
    required: false,
    default: null,
  },
});
const emit = defineEmits<{
  (e: 'refetchWithPersona', persona: string): void;
  (
    e: 'refetchWithRefinement',
    payload: { persona: string; refinementAnswers: Record<string, string> },
  ): void;
  (e: 'exploreNearby'): void;
}>();
const neighbourhoodStore = useNeighbourhoodStore();
const isMounted = ref(false);
const contentKey = ref(0);
const selectedPersona = computed({
  get: () => neighbourhoodStore.selectedPersona,
  set: (value: PersonaType) => neighbourhoodStore.setSelectedPersona(value),
});
const hasTrackedFeatureDisplay = ref(false);
const personaConfigs = Object.values(PERSONA_CONFIGS);

// Ref for the section element to observe visibility
const sectionRef = ref<HTMLElement | null>(null);
const intersectionObserver = ref<IntersectionObserver | null>(null);

const currentInsightsData = computed(() => {
  if (props.neighbourhoodData?.data) {
    try {
      return transformPOIResponseToComponentFormat(props.neighbourhoodData);
    } catch (error) {
      return null;
    }
  }

  return null;
});

const statsWithPlaceholder = computed(() => {
  if (!currentInsightsData.value) {
    return [];
  }

  const stats = getStatsArray(currentInsightsData.value.statsCard);
  const placeholderValue = t('neighbourhood.insights.statsPlaceholder');

  return stats.map((stat, index) => {
    const normalizedValue = stat.value?.trim().toLowerCase() ?? '';
    const personaKeys = PERSONA_STATS_TRANSLATION_KEYS[selectedPersona.value];
    const labelTranslationKey = personaKeys[index] ?? '';
    const isPlaceholder = PLACEHOLDER_VALUES.has(normalizedValue);

    return {
      ...stat,
      label: labelTranslationKey,
      value: isPlaceholder ? placeholderValue : stat.value,
    };
  });
});
// Track feature_displayed when user scrolls to view section (AFX-4471)
const setupIntersectionObserver = () => {
  if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return;
  if (!sectionRef.value || hasTrackedFeatureDisplay.value) return;

  intersectionObserver.value = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && props.neighbourhoodData && !hasTrackedFeatureDisplay.value) {
        trackNeighbourhoodFeatureDisplayed();
        hasTrackedFeatureDisplay.value = true;
        intersectionObserver.value?.disconnect();
      }
    },
    { threshold: 0.1 },
  );
  intersectionObserver.value.observe(sectionRef.value);
};

const cleanupIntersectionObserver = () => {
  intersectionObserver.value?.disconnect();
  intersectionObserver.value = null;
};

onMounted(() => {
  isMounted.value = true;
  setupIntersectionObserver();
});

onBeforeUnmount(() => {
  cleanupIntersectionObserver();
});

watch(
  () => props.neighbourhoodData,
  (newData) => {
    if (newData && !hasTrackedFeatureDisplay.value) {
      setupIntersectionObserver();
    }
  },
);
watch(selectedPersona, (newPersona) => {
  contentKey.value++;
  emit('refetchWithPersona', getPersonaConfig(newPersona).label);
  // Track persona change - map PersonaType to tracking format
  const trackingPersona =
    newPersona === PersonaType.Students ? 'student' : newPersona.toLowerCase();
  trackNeighbourhoodPersonaChange(trackingPersona as 'family' | 'professional' | 'student');
});
const handlePersonaClick = (persona: PersonaType) => {
  selectedPersona.value = persona;
};
const getPersonaIcon = (persona: PersonaType): string => {
  const iconMap: Record<PersonaType, string> = {
    [PersonaType.Family]: 'home',
    [PersonaType.Professional]: 'user',
    [PersonaType.Students]: 'book-open',
  };
  return iconMap[persona] || 'home';
};
const getStatsArray = (statsCard: {
  stat1: StatCard;
  stat2: StatCard;
  stat3: StatCard;
}): StatCard[] => {
  return [statsCard.stat1, statsCard.stat2, statsCard.stat3];
};
const handleExploreNearby = () => {
  emit('exploreNearby');

  trackNeighbourhoodExploreNearby();
};
</script>
<style module lang="scss">
@import '@/assets/variables';

.neighbourhoodSection {
  margin: $space-300 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.grayCard {
  background: #f5f5f5;
  border-radius: 12px;
  padding: 24px;
  container-type: inline-size;
}

.personaToggle {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.personaButton {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 16px;
  background: #ffffff;
  border: 1px solid #595959;
  border-radius: 999px;
  font-size: 16px;
  font-weight: 800;
  color: #333333;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px 0 rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled):not(.active) {
    background: #f9fafb;
  }

  &.active {
    background: #333333;
    border-color: #595959;
    color: white;
    font-weight: 800;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    .personaIcon {
      color: white;
    }

    &:hover:not(:disabled) {
      background: #333333;
      border-color: #595959;
      color: white;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.personaButtonSkeleton {
  background: #e5e7eb;
  border-color: #e5e7eb;
  color: transparent;
  cursor: default;

  &:hover:not(:disabled):not(.active) {
    background: #e5e7eb;
  }

  &.active {
    background: #e5e7eb;
    border-color: #e5e7eb;
    color: transparent;
    box-shadow: none;

    .personaIcon {
      color: transparent;
    }

    &:hover:not(:disabled) {
      background: #e5e7eb;
      border-color: #e5e7eb;
      color: transparent;
    }
  }

  .personaIcon {
    opacity: 0;
  }
}

.personaIcon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
}

.loadingSkeleton {
  display: flex;
  flex-direction: column;
  gap: $space-150;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }
}

.skeletonHighlights {
  padding: 24px;
  background: #e0f2fe;
  border-radius: 12px;
  margin-bottom: 24px;
  min-height: 80px;
}

.skeletonHighlightsHeader {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.skeletonTwoColumn {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;

  @container (max-width: #{$neighbourhood-single-col-max}) {
    grid-template-columns: 1fr;
  }
}

.skeletonColumnCard {
  padding: 24px;
  border-radius: 12px;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  min-height: 280px;
}

.skeletonColumnTitle,
.skeletonColumnTitleWide {
  height: 12px;
  background: #e5e7eb;
  border-radius: 4px;
  margin-bottom: 16px;
}

.skeletonColumnTitle {
  width: 40%;
}

.skeletonColumnTitleWide {
  width: 70%;
}

.skeletonList,
.skeletonPlacesList,
.skeletonPlaceLines {
  display: flex;
  flex-direction: column;
}

.skeletonList {
  gap: 14px;
}

.skeletonPlacesList {
  gap: 18px;
  margin-bottom: 16px;
}

.skeletonPlaceLines {
  flex: 1;
  gap: 6px;
}

.skeletonListItem,
.skeletonPlaceItem {
  display: flex;
  gap: 12px;
}

.skeletonListItem {
  align-items: center;
}

.skeletonPlaceItem {
  align-items: flex-start;
}

.skeletonBadges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.skeletonBadge {
  width: 90px;
  height: 20px;
  background: #e5e7eb;
  border-radius: 999px;
}

.skeletonStatsRow {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;

  @container (max-width: #{$neighbourhood-stats-single-col-max}) {
    grid-template-columns: 1fr;
  }
}

.skeletonStatCard {
  min-height: 100px;
  background: #e5e7eb;
  border-radius: 8px;
}

.skeletonFeedback {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
  margin-top: 20px;
}

.skeletonLine,
.skeletonLineMedium,
.skeletonLineShort {
  height: 14px;
  background: #e5e7eb;
  border-radius: 4px;
}

.skeletonLine {
  width: 100%;
}

.skeletonLineMedium {
  width: 55%;
}

.skeletonLineShort {
  width: 75%;
}

.skeletonIcon,
.skeletonCircle {
  background: #e5e7eb;
  flex-shrink: 0;
}

.skeletonIcon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
}

.skeletonCircle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
}

.fadeIn {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.noData {
  padding: $space-200;
  text-align: center;
  color: #6b7280;
  font-size: 0.9375rem;
}
</style>
